import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { fetchDocuments, uploadDocument, deleteDocument } from '../../../Redux/ActionCreators/onboardingAction';
import '../onboarding.scss';
import { Table } from 'react-bootstrap';
import { useAppPermission } from '../../../hooks/useAppPermission';
import { P } from '../../../constants/rbac';

// ─── Constants & Helpers ──────────────────────────────────────────────────────

const formatSize = (bytes) => {
  if (!bytes)               return '—';
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.toLocaleString('en-GB', { month: 'long' });
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${hours}:${mins}`;
};

const resolveFileType = (name = '', mimeType = '') => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || mimeType.includes('pdf'))
    return { color: '#991b1b', bg: '#fee2e2', label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext) || mimeType.includes('sheet') || mimeType.includes('excel') || mimeType.includes('csv'))
    return { color: '#15803d', bg: '#dcfce7', label: 'XLS' };
  if (['doc', 'docx'].includes(ext) || mimeType.includes('word') || mimeType.includes('doc'))
    return { color: '#1e40af', bg: '#dbeafe', label: 'DOC' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext) || mimeType.includes('image'))
    return { color: '#92400e', bg: '#fef3c7', label: 'IMG' };
  if (['txt', 'md', 'log'].includes(ext) || mimeType.includes('text') || mimeType.includes('plain'))
    return { color: '#374151', bg: '#f3f4f6', label: 'TXT' };
  if (['ppt', 'pptx'].includes(ext))
    return { color: '#c2410c', bg: '#ffedd5', label: 'PPT' };
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext))
    return { color: '#6d28d9', bg: '#ede9fe', label: 'ZIP' };
  return { color: '#6b7280', bg: '#f3f4f6', label: ext.toUpperCase().slice(0, 4) || 'FILE' };
};

const FileTypeIcon = ({ name, mimeType }) => {
  const { color, bg, label } = resolveFileType(name, mimeType);

  return (
    <svg width="36" height="42" viewBox="0 0 36 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 0h20l12 12v26a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z" fill={bg} />
      <path d="M24 0l12 12H28a4 4 0 0 1-4-4V0z" fill={color} fillOpacity="0.35" />
      <text x="18" y="30" textAnchor="middle" fill={color} fontSize="8.5" fontWeight="800" fontFamily="Arial, sans-serif" letterSpacing="0.5">{label}</text>
    </svg>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const UploadZone = ({ uploading, onUpload, fileRef }) => (
  <div
    className="ob-upload-zone"
    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#f75891'; e.currentTarget.style.background = 'rgba(247,88,145,0.08)'; }}
    onDragLeave={e => { e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = ''; }}
    onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = ''; e.currentTarget.style.background = ''; onUpload(e.dataTransfer.files); }}
    onClick={() => fileRef.current?.click()}
  >
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8a9199" strokeWidth="1.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
    <p>{uploading ? 'Uploading…' : 'Click or drag files to upload'}</p>
    <span>PDF, DOC, DOCX, XLS, PNG, JPG — max 20MB</span>
    <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={e => onUpload(e.target.files)} />
  </div>
);


const DeleteModal = ({ onConfirm, onCancel, deleting }) => (
  <div className="upload-modal">
    <div className="modal-content" style={{ width: '435px' }}>
      <div className="modal-head">
        <div></div>
        <h3>Delete Document</h3>
        <img src="/images/close-icon.svg" alt="" onClick={onCancel} style={{ cursor: 'pointer' }} title='Close' />
      </div>
      <div className="modal-input d-flex flex-column align-items-center" style={{ gap: '15px', padding: '15px' }}>
        <p>Are you sure you want to delete this document?</p>
      </div>
      <div className="d-flex justify-content-center">
        <button className="upload-btn" onClick={onConfirm} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
  const pages = [];
  pages.push(
    <button className="prev-btn" key="prev" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
      <img src="/images/leftarrow-icon.svg" alt="" />
    </button>
  );
  pages.push(
    <button key={1} className={currentPage === 1 ? 'active' : ''} onClick={() => setCurrentPage(1)}>{1}</button>
  );
  if (currentPage > 3) pages.push(<button key="left-dots">...</button>);
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) {
    pages.push(<button key={i} className={currentPage === i ? 'active' : ''} onClick={() => setCurrentPage(i)}>{i}</button>);
  }
  if (currentPage < totalPages - 2) pages.push(<button key="right-dots">...</button>);
  if (totalPages > 1) {
    pages.push(
      <button key={totalPages} className={currentPage === totalPages ? 'active' : ''} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
    );
  }
  pages.push(
    <button className="next-btn" key="next" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
      <img src="/images/leftarrow-icon.svg" alt="" style={{ rotate: "180deg" }} />
    </button>
  );
  return pages;
};

// ─── Org Files Tab ────────────────────────────────────────────────────────────

const OrgFiles = () => {
  const dispatch = useDispatch();
  const { can } = useAppPermission();
  const canCreate = can(P.ONBOARDING_FLOW_CREATE);
  const canDelete = can(P.ONBOARDING_FLOW_DELETE);
  const fileRef = useRef();
  const debounceRef = useRef(null);

  const [docs, setDocs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId]   = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: '', severity: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(10);
  const [pagination, setPagination]   = useState({ total: 0, page: 1, page_size: 10 });
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const showActions = canDelete || docs.some((d) => d.file_url);

  const loadDocs = useCallback(async (page, size, searchTerm = '') => {
    setLoading(true);
    const params = searchTerm ? `search=${encodeURIComponent(searchTerm)}` : '';
    const res = await dispatch(fetchDocuments('org', page, size, params));
    if (res) {
      setDocs(res.data || []);
      setPagination(res.pagination || { total: (res.data || []).length, page, page_size: size });
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => { loadDocs(currentPage, pageSize, debouncedSearch); }, [loadDocs, currentPage, pageSize, debouncedSearch]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);

    let successCount = 0;
    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('bucket', 'org');
      formData.append('badge', 'org');
      formData.append('detail', `${formatSize(file.size)} · uploaded to Azure Blob`);

      const result = await dispatch(uploadDocument(formData));

      if (!result?.success) {
        const detail = Array.isArray(result?.detail) ? result.detail.join(', ') : result?.detail;
        setSnackbar({ open: true, message: detail || result?.message || 'Upload failed', severity: 'error' });
      } else {
        successCount++;
      }
    }

    if (successCount > 0) {
      setSnackbar({ open: true, message: `${successCount} file${successCount > 1 ? 's' : ''} uploaded successfully.`, severity: 'success' });
    }

    setUploading(false);
    loadDocs(currentPage, pageSize, debouncedSearch);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await dispatch(deleteDocument(deleteId));
    setDeleteId(null);
    setDeleting(false);
    setSnackbar({ open: true, message: 'Document deleted successfully.', severity: 'success' });
    loadDocs(currentPage, pageSize, debouncedSearch);
  };

  const totalPages = Math.ceil((pagination.total || 0) / pageSize);

  return (
    <div>
      {canCreate && (
      <div className="ob-card" style={{ marginBottom: 16 }}>
        <UploadZone uploading={uploading} onUpload={handleUpload} fileRef={fileRef} />
      </div>
      )}

      <div className="ob-card" style={{ marginBottom: 16 }}>
        <input
          style={{ width: '100%', padding: '9px 12px', border: '1px solid #e4e9f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' }}
          placeholder="Search documents…"
          value={search}
          onChange={e => {
            const val = e.target.value;
            setSearch(val);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              setDebouncedSearch(val);
              setCurrentPage(1);
            }, 400);
          }}
        />
      </div>

      {loading ? (
        <div className="ob-spinner"><div className="spinner-border" role="status" /></div>
      ) : (
        <>
          <div className="ob-table-wrap">
            <Table responsive className="ob-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Added Date</th>
                  <th className='text-center'>Used In</th>
                  {showActions && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {docs.length === 0 ? (
                  <tr>
                    <td colSpan={showActions ? 4 : 3} style={{ textAlign: 'center', color: '#8a9199', padding: '32px 14px' }}>
                      {debouncedSearch ? 'No documents match your search.' : 'No files uploaded. Use the upload zone above to add documents.'}
                    </td>
                  </tr>
                ) : (
                  docs.map(doc => (
                    <tr key={doc.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <FileTypeIcon name={doc.name} mimeType={doc.mimeType} />
                          <span className="td-name">{doc.name}</span>
                        </div>
                      </td>
                      <td>{doc.created_at ? formatDateTime(doc.created_at) : '—'}</td>
                      <td className='text-center'>{doc.dependent_count ?? 0}</td>
                      {showActions && (
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {doc.file_url && (
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ob-btn ob-btn--sm ob-btn--icon-only"
                              title="Download"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </a>
                          )}
                          {canDelete && (
                          <button
                            className="ob-btn ob-btn--sm ob-btn--danger ob-btn--icon-only"
                            onClick={() => setDeleteId(doc.id)}
                            title={doc.can_delete === false ? (doc.can_delete_hint || 'Cannot delete: document is in use') : 'Delete'}
                            disabled={doc.can_delete === false}
                            style={{ border: '1px solid #EF44441A', opacity: doc.can_delete === false ? 0.45 : 1, cursor: doc.can_delete === false ? 'not-allowed' : 'pointer' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14H6L5 6" />
                              <path d="M10 11v6M14 11v6" />
                              <path d="M9 6V4h6v2" />
                            </svg>
                          </button>
                          )}
                        </div>
                      </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {pagination.total > 0 && (
            <div className="pagination d-flex flex-wrap align-items-center justify-content-between" style={{ marginTop: 12 }}>
              <div className="d-flex flex-wrap" style={{ gap: '10px' }}>
                <div className="d-flex flex-wrap" style={{ gap: '20px' }}>
                  <div className="pagination-info d-flex align-items-center">
                    <span style={{ color: '#525B66' }}>
                      Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, pagination.total)} of <strong>{pagination.total}</strong> Files
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    {renderCustomPagination(totalPages, currentPage, setCurrentPage)}
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span style={{ color: '#525B66', lineHeight: '100%' }}>Files per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                    style={{ width: '46px', height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {deleteId && (
        <DeleteModal
          onConfirm={handleDelete}
          onCancel={() => setDeleteId(null)}
          deleting={deleting}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

// ─── Document Center ──────────────────────────────────────────────────────────

const DocumentCenter = () => (
  <div className="ob-page">
    <div className="ob-page__header">
      <div className="ob-page__header-left">
        <h2>Document Center</h2>
      </div>
    </div>

    <OrgFiles />
  </div>
);

export default DocumentCenter;
